import * as React from 'react'
import { useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import { Element } from './warehouse_rent_list_types'

interface QuantityModalProps {
  isOpen: boolean
  element: Element | null
  maxQuantity: number
  currentQuantity: number
  isParentMissing: boolean
  onConfirm: (quantity: number) => void
  onCancel: () => void
}

function QuantityModal({ 
  isOpen, 
  element, 
  maxQuantity, 
  currentQuantity,
  isParentMissing,
  onConfirm, 
  onCancel 
}: QuantityModalProps): React.ReactElement | null {
  const [quantity, setQuantity] = useState(1)
  const { I18n } = window as any

  React.useEffect(() => {
    if (isOpen) {
      setQuantity(currentQuantity > 0 ? currentQuantity : 1)
    }
  }, [isOpen, currentQuantity])

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel()
      } else if (e.key === 'Enter') {
        const minQuantity = currentQuantity > 0 ? 0 : 1
        if (!isParentMissing && quantity >= minQuantity && quantity <= maxQuantity) {
          handleConfirm()
        }
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, quantity, maxQuantity, currentQuantity, isParentMissing])

  if (!element) return null

  const handleConfirm = () => {
    onConfirm(quantity)
  }

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 0
    const minQuantity = currentQuantity > 0 ? 0 : 1
    if (value >= minQuantity && value <= maxQuantity) {
      setQuantity(value)
    }
  }

  const modalTitle = currentQuantity > 0 
    ? I18n.t('elements.warehouse_rent_list.elements.edit_quantity')
    : I18n.t('elements.warehouse_rent_list.elements.select_quantity')

  return (
    <Modal isOpen={isOpen} toggle={onCancel} size="md">
      <ModalHeader toggle={onCancel}>
        {modalTitle}
      </ModalHeader>
      <ModalBody>
        <p><strong>{element.name}</strong></p>
        <div className="mb-3">
          <p><strong>{I18n.t('elements.warehouse_rent_list.elements.total_amount')}: {element.amount}</strong></p>
          <p className="text-muted small mb-1">
            • {I18n.t('elements.warehouse_rent_list.elements.reserved_elsewhere')}: {element.amount - maxQuantity}
          </p>
          <p className="text-muted small mb-1">
            • {I18n.t('elements.warehouse_rent_list.elements.current_quantity')}: {currentQuantity}
          </p>
          <p className="text-success small">
            • {I18n.t('elements.warehouse_rent_list.elements.available_for_this_period')}: {maxQuantity - currentQuantity}
          </p>
        </div>
        <div className="mb-3">
          {isParentMissing && (
            <p className="text-warning small mb-2">
              {I18n.t('elements.warehouse_rent_list.elements.parent_required_warning')}
            </p>
          )}
          <label htmlFor="quantity-input" className="form-label">
            {I18n.t('elements.warehouse_rent_list.elements.quantity', { default: 'Количество' })}
          </label>
          <input
            id="quantity-input"
            type="number"
            className="form-control"
            value={quantity}
            onChange={handleQuantityChange}
            min={currentQuantity > 0 ? 0 : 1}
            max={maxQuantity}
            autoFocus
            disabled={isParentMissing}
          />
          {quantity > maxQuantity && (
            <div className="text-danger small mt-1">
              {I18n.t('elements.warehouse_rent_list.elements.quantity_exceeds_available')}
            </div>
          )}
          {quantity === 0 && currentQuantity > 0 && (
            <div className="text-info small mt-1">
              {I18n.t('elements.warehouse_rent_list.elements.quantity_zero_removes')}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          {I18n.t('elements.warehouse_rent_list.elements.cancel')}
        </button>
        <button 
          type="button" 
          className="btn btn-primary" 
          onClick={handleConfirm}
          disabled={isParentMissing || quantity < (currentQuantity > 0 ? 0 : 1) || quantity > maxQuantity}
        >
          {quantity === 0 && currentQuantity > 0
            ? I18n.t('elements.warehouse_rent_list.elements.remove')
            : I18n.t('elements.warehouse_rent_list.elements.ok')
          }
        </button>
      </ModalFooter>
    </Modal>
  )
}

export default QuantityModal
